/* eslint-disable max-len */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Checkbox,
  Icon,
  Label,
  Popup,
  Table,
} from 'semantic-ui-react';
import { withTranslation } from 'react-i18next';
import CurrencySymbol from './CurrencySymbol';
import LoanAmount from './LoanAmount';
import { TRANSLATION_FILES } from './../constants/translation';
import { isPricingPackageLocked, vnPackageLockMessage } from '../utils/vietnamPricing';

class BookingFeePackage extends Component {
  static propTypes = {
    availablePackages: PropTypes.arrayOf(PropTypes.shape()),
    data: PropTypes.shape(),
    onCheckRecalculate: PropTypes.func,
    isEditing: PropTypes.bool,
    auth: PropTypes.shape({
      isSuperAdmin: PropTypes.bool,
      isAdminBasic: PropTypes.bool,
      isAdmin: PropTypes.bool,
      isDeveloperSupervisor: PropTypes.bool,
      isDeveloperSalesManager: PropTypes.bool,
      isDeveloperSalesAdmin: PropTypes.bool,
      isHeadquarters: PropTypes.bool,
    }),
    openModal: PropTypes.func,
    t: PropTypes.func,
  }
  hasPackage = data => data.packageFee && data.packageFee.id !== ''
  hasRecalculateAccess = () => {
    const { auth } = this.props;
    if (auth.isAdminBasic || auth.isSuperAdmin) return true;
    return false;
  }
  hasEditPackageAccess = () => {
    const { auth, data } = this.props;
    return (data.statusID === 'sold_spa_signed' &&
      (auth.isAdmin || auth.isAdminBasic || auth.isSuperAdmin)) ||
      auth.isDeveloperSupervisor ||
      auth.isDeveloperSalesManager ||
      auth.isDeveloperSalesAdmin ||
      auth.isHeadquarters;
  }
  disablePricingEditAfterSold = data => data && data.statusID === 'sold_spa_signed' && data.project && !data.project.allowPricingEditAfterSold
  // CPI-977 requirements 7/11: Vietnam locks the fee package on the same rule as the pricing package.
  isVNPackageLocked = () => isPricingPackageLocked(this.props.auth, this.props.data && this.props.data.statusID, 'fee')
  handleRecalculate = (e, data) => {
    if (this.props.onCheckRecalculate) {
      this.props.onCheckRecalculate(data.checked);
    }
  }
  renderDescription = (itemDesc) => {
    let iDesc = itemDesc;
    if (iDesc.includes('\n')) {
      iDesc = iDesc.split('\n').map((desc, idx) => <span key={idx}>{desc}<br /></span>);
    }
    return iDesc;
  }

  renderTextButton = (title, name, placement) => {
    const { data, t } = this.props;
    if (this.isVNPackageLocked()) {
      return null;
    }
    return (
      <Popup
        trigger={
          <Label
            as="button"
            attached={placement || undefined}
            size="small"
            content={title}
            onClick={() => this.props.openModal && this.props.openModal(name, true)}
            className="textButton"
            disabled={this.disablePricingEditAfterSold(data)}
          />
        }
        disabled={!this.disablePricingEditAfterSold(data)}
        content={t('Pricing details are locked because the booking has been marked as Sold', { ns: TRANSLATION_FILES.translation })}
      />
    );
  };

  renderHeader = () => {
    const { data, availablePackages, t } = this.props;
    return (
      <span>
        {
          this.hasPackage(data) ? (
            <span>
              {t('Additional Fee Package')}:&nbsp;
              <span className="text_name italic">
                {data.packageFee.name}
              </span>
            </span>
          ) : <span>{t('No Additional Fees added')}</span>
        }
        {
          (this.hasEditPackageAccess() && availablePackages && availablePackages.length > 0) &&
          this.renderTextButton(t(`${this.hasPackage(data) ? 'Edit' : 'Add'} Package`), 'showEditFeePackageModal')
        }
        {
          this.isVNPackageLocked() && (
            <span className="text_formula_small">
              &nbsp;<Icon name="lock" size="small" />
              {t(vnPackageLockMessage(this.props.auth, data && data.statusID, 'fee'))}
            </span>
          )
        }
      </span>
    );
  };

  renderFeePackageBreakdown = () => {
    const { data } = this.props;
    const projectCurrencies = data.project.projectCurrency || null;
    const items = data && data.packageFee && data.packageFee.items ? data.packageFee.items : [];
    return (
      items.filter(c => c.isEnabled).map((calc, idx) => (
        <Table.Row key={idx} className="row_formula_items">
          <Table.Cell className="text_formula_small">{calc.name}</Table.Cell>
          <Table.Cell className="text_description">{this.renderDescription(calc.description)}</Table.Cell>
          <Table.Cell textAlign="right">
            <div>
              <div className="div_inline_block">
                <Icon color="green" size="small" name="plus" />
              </div>
              <div className="div_inline_block text_add">
                <LoanAmount
                  precise={false}
                  projectCurrencies={projectCurrencies}
                  format="0,000,000.00"
                  amount={calc.valueChange}
                  prefix={<CurrencySymbol />}
                />
              </div>
            </div>
          </Table.Cell>
        </Table.Row>
      ))
    );
  }

  render() {
    const { data, isEditing, t } = this.props;
    const projectCurrencies = data.project.projectCurrency || null;
    const hasFormulas = data && data.packageFee && data.packageFee.items && data.packageFee.items.length > 0;
    return (
      <div className="bookingPricingPackage">
        {
          data && data.packageFee && !data.packageFee.isCustomPrice &&
          this.hasEditPackageAccess() && (
            this.renderTextButton(t('Customise formula(s)'), 'showCustomiseFeePackageFormulaModal', 'top right')
         )
        }
        <Table compact unstackable structured>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell colSpan={3}>
                {this.renderHeader()}
                {(isEditing && this.hasRecalculateAccess()) ?
                  <div className="mobile-hide tablet-hide div_recalculate">
                    <Checkbox
                      // eslint-disable-next-line jsx-a11y/label-has-for
                      label={<label>{t('Force recalculate additional fee package')} <Icon name="warning sign" /></label>}
                      onChange={this.handleRecalculate}
                      disabled={this.disablePricingEditAfterSold(data)}
                    />
                  </div>
                  : ''
                }
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {this.hasPackage(data) ? (
              <Table.Row>
                <Table.Cell colSpan={3}>
                  <span className="text_description">
                    {data.packageFee ? this.renderDescription(data.packageFee.description) : ''}
                  </span>
                </Table.Cell>
              </Table.Row>
            ) : null}
            {this.hasPackage(data) ? (
              <Table.Row negative>
                <Table.Cell colSpan={3} />
              </Table.Row>
            ) : null}
            {hasFormulas ? this.renderFeePackageBreakdown() : null}
            <Table.Row negative>
              <Table.Cell colSpan={3} />
            </Table.Row>
            <Table.Row className="row_total">
              <Table.Cell colSpan={2}>
                {t('Total Additional Fees')}
              </Table.Cell>
              <Table.Cell textAlign="right">
                {
                  (data.packageFee ?
                    <LoanAmount
                      precise={false}
                      projectCurrencies={projectCurrencies}
                      format="0,000,000.00"
                      amount={data.packageFee.totalPrice}
                      prefix={<CurrencySymbol />}
                    />
                    : '-')
                }
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
      </div>
    );
  }
}
export default withTranslation(TRANSLATION_FILES.screens)(BookingFeePackage);



// WEBPACK FOOTER //
// ./src/components/BookingFeePackage.jsx